/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ProcessingJob,
  ProcessingJobAbstract,
  PageOutput,
  ExtractedDataOutput,
  PiiOutput,
  DocumentTypeOutput,
  IdImageOutput,
  RiskAssessmentOutput,
  RiskAssessment,
  CrossMatchResult,
  CrossMatchDataItem,
  DataMatchGroup,
  DocumentDataSource,
} from 'types/proccesingJobs';
import SettingsDirectAccess from 'store/settings/directAccess';
import DataExtractionDefinitionsDirectAccess from 'store/dataExtractionDefinitions/directAccess';
import { CrossMatchDataItemGroup } from '../../../types/proccesingJobs';

interface PagePiiOutput {
  pageNumber: number;
  piis: PiiOutput[];
}

interface PageDataPointsOutput {
  pageNumber: number;
  dataPoints: ExtractedDataOutput[];
  groupedDataPoints: ExtractedDataOutput[][];
}

interface PageIdImageOutput {
  pageNumber: number;
  idImages: IdImageOutput[];
}

const extractDocumentType = (output: any): DocumentTypeOutput | undefined => {
  if (output) {
    return {
      documentType: output.Value,
      version: output.Version,
      subDocumentType: output.SubValue,
      subVersion: output.SubVersion,
    };
  }

  return undefined;
};

const extractRisks = (output: any[]): RiskAssessmentOutput[] | undefined => {
  if (!output) {
    return [];
  }

  return output.map((data) => ({
    personFullName: data.PersonFullName,
    personRiskLevel: mapRiskLevel(data.PersonRiskLevel),
    searchPerformedAt: data.SearchPerformedAt,
    riskAssessments: extractRiskAssessments(data.RiskAssessments) ?? [],
  }));
};

const extractRiskAssessments = (output: any[]): RiskAssessment[] | undefined => {
  if (!output) {
    return [];
  }

  return output.map((data) => ({
    keywords: [...data.Keywords],
    searchDescription: data.SearchDescription,
    searchUrl: data.SearchUrl,
    searchTitle: data.SearchTitle,
  }));
};

const extractIdExistance = (output: any): number[] | undefined => {
  const idPredictions = output as { [key: string]: boolean };
  if (idPredictions) {
    return Object.entries(idPredictions)
      .filter(([_, value]) => !!value)
      .map(([key]) => parseInt(key, 10));
  }

  return undefined;
};

const extractPii = (output: any): PagePiiOutput[] | undefined => {
  const idPredictionsPerPage = output as { [key: string]: any[] };
  if (idPredictionsPerPage) {
    if (Object.values(idPredictionsPerPage).every((x) => x == null)) {
      return undefined;
    }

    return Object.entries(idPredictionsPerPage).map(([key, value]) => ({
      pageNumber: parseInt(key, 10),
      piis: (value || []).map((piiExtraction) => ({
        firstName: piiExtraction.FirstName ?? '',
        lastName: piiExtraction.LastName ?? '',
        nationality: {
          name: piiExtraction.Nationality?.Name ?? '',
          threeLetterCode: piiExtraction.Nationality?.ThreeLetterCode ?? '',
        },
        documentNumber: piiExtraction.DocumentNumber ?? '',
        personalNumber: piiExtraction.PersonalNumber ?? '',
        gender: piiExtraction.Gender ?? '',
        dateOfBirth: piiExtraction.DateOfBirth?.Parsed ?? '',
        issuingCountry: {
          name: piiExtraction.IssuingCountry?.Name ?? '',
          threeLetterCode: piiExtraction.IssuingCountry?.ThreeLetterCode ?? '',
        },
        expirationDate: piiExtraction.ExpirationDate?.Parsed ?? '',
        documentType: piiExtraction.DocumentType ?? '',
        documentSubType: piiExtraction.DocumentSubType ?? '',
        residencePermitStartDate: piiExtraction.ResidencePermitStartDate?.Parsed ?? '',
        issuingAuthority: piiExtraction.IssuingAuthority ?? '',
        placeOfBirth: piiExtraction.PlaceOfBirth ?? '',
        mrz: piiExtraction.Mrz ?? '',
        fullFirstName: piiExtraction.FullFirstName ?? '',
        dateOfIssue: piiExtraction.DateOfIssue?.Parsed ?? '',
      })),
    }));
  }

  return undefined;
};

const extractDataPointsFromGrouped = (output: any): PageDataPointsOutput[] | undefined => {
  const dataPointExtractionsPerPage = output as { [key: string]: any[] };
  if (dataPointExtractionsPerPage) {
    try {
      return Object.entries(dataPointExtractionsPerPage).map(([key, value]) => ({
        pageNumber: parseInt(key, 10),
        dataPoints: mapDataPointExtractions(value),
        groupedDataPoints: groupDataPointsByGroupId(value),
      }));
    } catch {
      return undefined;
    }
  }

  return undefined;
};

const extractDataPointsFromNotGrouped = (output: any): PageDataPointsOutput[] | undefined => {
  const dataPointExtractions = output as any[];
  if (dataPointExtractions) {
    try {
      const dataPointExtractionsList = mapDataPointExtractions(dataPointExtractions);
      const groupedDataPointExtractionsList = groupDataPointsByGroupId(dataPointExtractions);

      const pageNumbers = dataPointExtractionsList.map((x) => x.pageNumber);
      const pageNumbersGrouped = groupedDataPointExtractionsList.map((x) => x[0].pageNumber);
      const uniquePageNumbers = [...new Set([...pageNumbers, ...pageNumbersGrouped])].sort(
        (n1, n2) => n1 - n2,
      );

      const dataPointExtractionsPerPage = uniquePageNumbers.map((pageNumber) => ({
        pageNumber,
        dataPoints: dataPointExtractionsList.filter((x) => x.pageNumber === pageNumber),
        groupedDataPoints: groupedDataPointExtractionsList.filter(
          (x) => x[0].pageNumber === pageNumber,
        ),
      }));

      return dataPointExtractionsPerPage;
    } catch {
      return undefined;
    }
  }

  return undefined;
};

const mergeDataPoints = (
  pageDataPoints1: PageDataPointsOutput[] | undefined,
  pageDataPoints2: PageDataPointsOutput[] | undefined,
): PageDataPointsOutput[] | undefined => {
  if (!pageDataPoints1) return pageDataPoints2;
  if (!pageDataPoints2) return pageDataPoints1;

  return pageDataPoints1.map((pageDataPoint) => {
    const otherPageDataPoints = pageDataPoints2.filter(
      (x) => x.pageNumber === pageDataPoint.pageNumber,
    );
    const dataPoints = [...pageDataPoint.dataPoints];
    if (otherPageDataPoints.length > 0) {
      dataPoints.push(...otherPageDataPoints[0].dataPoints);
    }
    const groupedDataPoints = [...pageDataPoint.groupedDataPoints];
    if (otherPageDataPoints.length > 0) {
      groupedDataPoints.push(...otherPageDataPoints[0].groupedDataPoints);
    }

    return {
      pageNumber: pageDataPoint.pageNumber,
      dataPoints,
      groupedDataPoints,
    };
  });
};

const extractGlobalDataPoints = (
  pageDataPoints: PageDataPointsOutput[] | undefined,
): ExtractedDataOutput[] | undefined => {
  if (pageDataPoints) {
    const globalPageDataPoints = pageDataPoints.filter((x) => x.pageNumber === -1);
    if (globalPageDataPoints.length > 0) {
      return globalPageDataPoints[0].dataPoints;
    }
  }

  return undefined;
};

const extractIdImages = (output: any): PageIdImageOutput[] | undefined => {
  const idImagesPerPage = output as { [key: string]: any[] };
  if (idImagesPerPage) {
    return Object.entries(idImagesPerPage).map(([key, value]) => ({
      pageNumber: parseInt(key, 10),
      idImages: value.map((data) => ({
        type: data.IdType,
        subType: data.IdSubType,
        fileId: data.PdfFileId,
      })),
    }));
  }

  return undefined;
};

const mapDataPointExtractions = (output: any[]): ExtractedDataOutput[] => {
  if (!output) return [];

  return output.filter((data) => data.GroupId === null).map((data) => mapDataPointExtraction(data));
};

const mapDataPointExtraction = (data: any): ExtractedDataOutput => {
  const dataExtractionDefinitions =
    DataExtractionDefinitionsDirectAccess.getDataExtractionDefinitions().detectedObjectTypes
      ?.detectedObjectTypes ?? {};
  const extractDataOutput: ExtractedDataOutput = {
    pageNumber: data.PageNumber,
    type: dataExtractionDefinitions[data.Type] ?? data.Type,
    value: data.ParsedValue,
    groupId: data.GroupId,
  };

  return extractDataOutput;
};

const mapCrossMatchResults = (data: any): CrossMatchResult | undefined => {
  if (!data) {
    return undefined;
  }

  const nonMatchingMatches = getNonMatchingValues(
    data.DataSet,
    data.MatchlessDataItemsReferenceIndices,
  );

  const crossMatchResults: CrossMatchResult = {
    dataset: data.DataSet.map(mapDataSet),
    matches: [...data.Matches.map(mapMatches), ...nonMatchingMatches],
    matchlessDataItemsReferenceIndices: data.MatchlessDataItemsReferenceIndices,
  };
  return crossMatchResults;
};

const getNonMatchingValues = (dataSet: any, matchlessData: any): DataMatchGroup[] => {
  const results: DataMatchGroup[] = [];

  const groupedMatchlessItems = groupMatchelessIndices(dataSet, matchlessData);
  groupedMatchlessItems.forEach((groupedMatchlessItem) => {
    const match: DataMatchGroup = {
      dataType: groupedMatchlessItem.dataType,
      value: groupedMatchlessItem.crossMatchDataItems.map((item) => item.value).join(' | '),
      matchType: 2,
      dataSetReferenceIndices: groupedMatchlessItem.groupedMissmatchIndices,
    };

    results.push(match);
  });
  return results;
};

const groupMatchelessIndices = (
  dataSet: any,
  matchlessIndices: number[],
): CrossMatchDataItemGroup[] => {
  const results: CrossMatchDataItemGroup[] = [];

  matchlessIndices.forEach((matchlessIndice) => {
    const dataSetItem = dataSet[matchlessIndice];
    const mappedDataSetItem = mapDataSet(dataSetItem);
    const existingGroup = results.find((result) => dataSetItem.DataType === result.dataType);
    if (existingGroup) {
      existingGroup.crossMatchDataItems.push(mappedDataSetItem);
      existingGroup.groupedMissmatchIndices.push(matchlessIndice);
    } else {
      const newCrossMatchDataItemGroup: CrossMatchDataItemGroup = {
        crossMatchDataItems: [mappedDataSetItem],
        dataType: mappedDataSetItem.dataType,
        groupedMissmatchIndices: [matchlessIndice],
      };

      results.push(newCrossMatchDataItemGroup);
    }
  });

  return results;
};

const mapMatches = (data: any): DataMatchGroup => {
  const match: DataMatchGroup = {
    dataType: data.MatchName,
    value: data.Value,
    matchType: data.MatchType,
    dataSetReferenceIndices: data.DataSetReferenceIndices,
  };

  return match;
};

const mapDataSet = (data: any): CrossMatchDataItem => {
  const dataSet: CrossMatchDataItem = {
    dataType: data.DataType,
    value: data.Value,
    documentDataSource: mapDocumentDataSource(data?.DocumentDataSource),
  };

  return dataSet;
};

const mapDocumentDataSource = (data: any): DocumentDataSource => {
  if (data === undefined || data === null) {
    const emptyDataSource: DocumentDataSource = {
      documentGroupDocumentId: '',
      documentName: '',
      documentType: 'External source input',
    };
    return emptyDataSource;
  }

  const crossMatchResults: DocumentDataSource = {
    documentGroupDocumentId: data.DocumentGroupDocumentId,
    documentName: data.DocumentName,
    documentType: data.DocumentType,
  };

  return crossMatchResults;
};

const mapRiskLevel = (riskLevel: number): string => {
  switch (riskLevel) {
    case 0:
      return 'None';
    case 1:
      return 'Low';
    case 2:
      return 'Medium';
    case 3:
      return 'High';
    default:
      return 'Risk level not found';
  }
};

const buildPerPage = (
  piiOutputs: PagePiiOutput[] | undefined,
  dataPointOutputs: PageDataPointsOutput[] | undefined,
  idImageOutputs: PageIdImageOutput[] | undefined,
): PageOutput[] | undefined => {
  if (!piiOutputs && !dataPointOutputs && !idImageOutputs) {
    return undefined;
  }

  const existingPiiOutputs = piiOutputs || [];
  const existingDataPointOutputs = dataPointOutputs || [];
  const existingIdImageOutputs = idImageOutputs || [];
  const filteredPiiOutputs = existingPiiOutputs.filter((x) => x.piis.length !== 0);
  const filteredDataPointOutputs = existingDataPointOutputs.filter(
    (x) => x.dataPoints.length !== 0 || x.groupedDataPoints.length !== 0,
  );
  const filteredIdImageOutputs = existingIdImageOutputs.filter((x) => x.idImages.length !== 0);

  const piiPages = filteredPiiOutputs.map((o) => o.pageNumber);
  const dataPointPages = filteredDataPointOutputs.map((o) => o.pageNumber);
  const idImagesPages = filteredIdImageOutputs.map((o) => o.pageNumber);
  const pages = [...new Set([...piiPages, ...dataPointPages, ...idImagesPages])].sort(
    (a, b) => a - b,
  );
  return pages.map((pageNumber) => {
    const pagePiis = filteredPiiOutputs.filter((x) => x.pageNumber === pageNumber);
    const pageDataPoints = filteredDataPointOutputs.filter((x) => x.pageNumber === pageNumber);
    const idImages = filteredIdImageOutputs.filter((x) => x.pageNumber === pageNumber);
    return {
      pageNumber,
      piis: pagePiis.length > 0 ? pagePiis[0].piis : [],
      dataPoints:
        pageDataPoints.length > 0 && pageDataPoints[0].dataPoints
          ? pageDataPoints[0].dataPoints
          : [],
      groupedDataPoints:
        pageDataPoints.length > 0 && pageDataPoints[0].groupedDataPoints
          ? pageDataPoints[0].groupedDataPoints
          : [],
      idImages: idImages.length > 0 ? idImages[0].idImages : [],
    };
  });
};

const groupDataPointsByGroupId = (output: any[] | undefined): ExtractedDataOutput[][] => {
  if (!output) return [];

  const groupedDataPoints: { [groupId: string]: ExtractedDataOutput[] } = {};

  output.forEach((data) => {
    const groupId = data.GroupId;
    if (groupId !== null && groupId !== undefined) {
      if (!groupedDataPoints[groupId]) {
        groupedDataPoints[groupId] = [];
      }
      groupedDataPoints[groupId].push(mapDataPointExtraction(data));
    }
  });

  const groupedPageDataPoints: ExtractedDataOutput[][] = Object.values(groupedDataPoints) ?? [];
  return groupedPageDataPoints;
};

export default (jobAbstract: ProcessingJobAbstract): ProcessingJob => {
  const propertiesMap = SettingsDirectAccess.getSettings().outputMap;
  const pdfFileId = (jobAbstract.output[propertiesMap.pdfFileId] as string) || undefined;
  const textFileId = (jobAbstract.output[propertiesMap.textFileId] as string) || undefined;
  const mergedFileId = (jobAbstract.output[propertiesMap.mergedFileId] as string) || undefined;
  const fileId = (jobAbstract.output[propertiesMap.fileId] as string) || undefined;
  const crossMatchResult = mapCrossMatchResults(
    (jobAbstract.output[propertiesMap.crossMatchResults] as string) || undefined,
  );
  const documentType = extractDocumentType(jobAbstract.output[propertiesMap.documentType]);
  const idExistanceInPages = extractIdExistance(jobAbstract.output[propertiesMap.idPredictions]);
  const piiExtractions = extractPii(jobAbstract.output[propertiesMap.pii]);
  const pageDataPoints1 = extractDataPointsFromGrouped(
    jobAbstract.output[propertiesMap.pagesDataExtraction],
  );
  const pageDataPoints2 = extractDataPointsFromNotGrouped(
    jobAbstract.output[propertiesMap.documentDataExtraction],
  );
  const pageDataPoints = mergeDataPoints(pageDataPoints1, pageDataPoints2);
  const dataPointsV2 = extractDataPointsFromNotGrouped(
    jobAbstract.output[propertiesMap.dataExtractionV2],
  );
  const dataPoints = mergeDataPoints(pageDataPoints, dataPointsV2);
  const documentDataPoints = extractGlobalDataPoints(dataPoints);

  const idImagesPerPage = extractIdImages(jobAbstract.output[propertiesMap.idCutOut]);
  const risks = extractRisks(jobAbstract.output[propertiesMap.riskAssessment]);
  return {
    processingJobId: jobAbstract.processingJobId,
    workflowId: jobAbstract.workflowId,
    status: jobAbstract.status,
    createdAt: jobAbstract.createdAt,
    finishedAt: jobAbstract.finishedAt,
    initialFileId: jobAbstract.initialFileIds[0],
    error: jobAbstract.error,
    fullOutputJson: JSON.stringify(jobAbstract.output, null, 2),
    output: {
      pdfFileId,
      textFileId,
      mergedFileId,
      documentType,
      idExistanceInPages,
      documentDataPoints,
      crossMatchResult,
      faceVerification: jobAbstract.output[propertiesMap.faceVerification] ?? '',
      perPage: buildPerPage(piiExtractions, dataPoints, idImagesPerPage),
      risks: risks ?? [],
      fileId,
    },
  };
};
